// Issue #203 — "expanded" display mode's per-row pills: one full pill per relevant
// participant (avatar, name, optional qty badge, own checkbox) instead of the small
// overlapping avatar-dot chips AssignmentChips renders in "condensed" mode. Both
// components share the same data-derivation (relevantParticipants/packingStateOf) but
// diverge on layout and on who may click which pill — see `clickable` below.

import type { CSSProperties, ReactNode } from "react";
import { Avatar, Checkbox, vars } from "@pack-it-up/ui-web";
import {
  allocationQuantityFor,
  packingStateOf,
  relevantParticipants,
  type Completable,
  type CompletionField,
  type PackingState,
  type ParticipantContext,
} from "@pack-it-up/core";
import type { ParticipantId, UserId } from "@pack-it-up/shared";

const pillStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "4px 6px 4px 4px",
  borderRadius: vars.radius.sm,
  // surfaceSunken sits too close in tone to the checkbox's own border color (they're
  // nearly the same gray), so an unchecked box was reading as borderless. surfaceRaised
  // gives the checkbox border enough contrast to actually read as a checkbox.
  background: vars.color.surfaceRaised,
  border: `1px solid ${vars.color.border}`,
};

// Checkbox.css bakes in a 2px top offset (an optical nudge for its usual home: inline
// next to a text baseline in a leading/trailing row slot). That offset reads as
// off-center once the checkbox is just another flex sibling in a short pill row, so
// cancel it back out here.
const checkboxWrapStyle: CSSProperties = { display: "inline-flex", marginTop: "-5px" };

const nameStyle: CSSProperties = {
  fontSize: "13px",
  color: vars.color.text,
  whiteSpace: "nowrap",
};

const qtyStyle: CSSProperties = {
  fontSize: "11px",
  color: vars.color.textMuted,
  background: vars.color.surface,
  padding: "1px 5px",
  borderRadius: vars.radius.xs,
};

const labelFor = (state: PackingState, pastTense: string): string =>
  state === "packed" ? pastTense : state === "staged" ? "staged" : `not ${pastTense}`;

export const ExpandedParticipantPills = ({
  item,
  list,
  actor,
  pastTense = "packed",
  canEdit = false,
  field = "completions",
  stagingEnabled = false,
  onToggle,
}: {
  // `quantity` is packing-item-only (todos have no quantity); it's the row's
  // item-level qty, the baseline an allocation's own qty badge diverges from below.
  item: Completable & { name: string; quantity?: number };
  // Trip or template (issue #188) — see AssignmentChips/CompletionControl.
  list: ParticipantContext;
  // The signed-in user, so their own pill is always toggleable regardless of
  // allowProxyCompletion — the item-level checkbox no longer covers that case in
  // expanded mode (there is no separate leading CompletionControl).
  actor: UserId;
  pastTense?: string;
  canEdit?: boolean;
  field?: CompletionField;
  stagingEnabled?: boolean;
  onToggle?: (target: ParticipantId, targetIsUser: boolean) => void;
}): ReactNode => {
  // A shared item has no per-participant breakdown — the row's trailing slot already
  // shows the single "Shared" tag (right-floated there, same as condensed mode), so
  // there's nothing to render here.
  if (item.assignment.kind === "shared") {
    return null;
  }

  const participants = relevantParticipants(item, list);
  return (
    <span style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>
      {participants.map((pid) => {
        const member = list.members[pid];
        if (member === undefined) return null;
        let state: PackingState;
        if (stagingEnabled) {
          state = packingStateOf(item, pid, true);
        } else {
          const map = field === "completions" ? item.completions : item.staging;
          state = (map?.byParticipant[pid]?.done ?? false) ? "packed" : "unstaged";
        }
        const name = member.profile.displayName;
        const qty = allocationQuantityFor(item.assignment, pid);
        // Badge only earns its keep when this participant's own allocation diverges
        // from the item-level qty shown on the row — not merely when it's non-1.
        const baseQty = item.quantity ?? 1;
        const clickable = member.kind === "managed" || pid === actor || list.settings.allowProxyCompletion;
        return (
          <span key={pid} style={pillStyle} title={`${name} — ${labelFor(state, pastTense)}`}>
            <Avatar name={name} size="xs" decorative />
            <span style={{ ...nameStyle, textDecoration: state === "packed" ? "line-through" : "none" }}>{name}</span>
            {qty !== undefined && qty !== baseQty && <span style={qtyStyle}>×{qty}</span>}
            <span style={checkboxWrapStyle}>
              <Checkbox
                checked={state === "packed"}
                {...(stagingEnabled ? { state } : {})}
                disabled={!(canEdit && clickable && onToggle !== undefined)}
                aria-label={`Mark ${name} as ${pastTense}`}
                onChange={() => onToggle?.(pid, member.kind === "user")}
              />
            </span>
          </span>
        );
      })}
    </span>
  );
};
